import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Logger } from '@saviynt/common';
import { type MenuMultiOption } from '@saviynt/design-system';

import { F_KEY_NM, F_KEY_ON } from '../../../shared/constants';
import { useTypedSelector } from '../../../shared/hooks';
import {
  useGetNameListMutation,
  useGetOwnersListMutation,
} from '../data-layer/serviceIdentityApi';
import { serviceIdentityActions } from '../data-store/serviceIdentitySlice';

export type OwnerType = 'user' | 'usergroup';

export interface OwnerApiItem {
  type: OwnerType;
  id: number;
  username: string;
  firstname: string | null;
  lastname: string | null;
  displayname: string | null;
  email: string | null;
  rank?: string | null;
  isChecked: boolean;
}

export interface OwnerApiResponse {
  success: boolean;
  data: OwnerApiItem[];
  nextCursor: string | null;
  msg: string;
}

export interface NameApiItem {
  name: string;
}

export interface NameApiResponse {
  data: NameApiItem[];
  nextCursor: string | null;
  total: number;
  success: boolean;
  msg: string;
}

/**
 * Manages loading filter options for API-based filters in the Service Identity
 * list. Supports the Owner (`F_KEY_ON`) and Name (`F_KEY_NM`) filters, both
 * with cursor-based pagination and checked-option preservation.
 *
 * @returns An object containing `loadFilterOptions` — call with a filter key and optional `isInit` flag.
 */
const useLoadFilterOptions = () => {
  const dispatch = useDispatch();
  const { filters } = useTypedSelector(
    (state) => state.savicopilot.serviceIdentity
  );

  // API hooks
  const [getOwnersList] = useGetOwnersListMutation();
  const [getNameList] = useGetNameListMutation();

  // Cursor state per filter key
  const cursorRef = useRef<Record<string, string | null>>({});

  /** Helper to update a single filter entry in Redux. */
  const updateFilter = useCallback(
    (
      fk: string,
      patch: Partial<{
        options: MenuMultiOption[];
        isLoading: boolean;
        isError: boolean;
        hasMoreOptions: boolean;
      }>
    ) => {
      dispatch(
        serviceIdentityActions.setFilters(
          filters.map((f) => (f.value === fk ? { ...f, ...patch } : f))
        )
      );
    },
    [dispatch, filters]
  );

  /**
   * Loads options for the Owner filter. Mirrors the pagination and
   * checked-option-preservation logic from `CreateServiceIdentity`.
   *
   * @param isInit - `true` for initial load or search change; `false` for scroll append.
   * @param searchOverride - When provided, uses this value instead of reading from Redux (avoids stale closures with debounced calls).
   */
  const loadOwnerFilterOptions = useCallback(
    (isInit: boolean, searchOverride?: string) => {
      const fk = F_KEY_ON;
      const currentFilter = filters.find((f) => f.value === fk);

      if (!currentFilter) {
        return;
      }

      const searchText = searchOverride ?? currentFilter.searchText ?? '';
      const isAppend = !isInit && cursorRef.current[fk] != null;

      // Set loading state
      updateFilter(fk, { isLoading: true, isError: false });

      // On fresh search / init, reset cursor and keep only checked options
      if (isInit) {
        cursorRef.current[fk] = null;
        updateFilter(fk, {
          isLoading: true,
          isError: false,
          options: currentFilter.options.filter((o) => o.isChecked),
        });
      }

      getOwnersList({
        search: searchText,
        max: 20,
        ...(cursorRef.current[fk] ? { cursor: cursorRef.current[fk] } : {}),
      })
        .unwrap()
        .then((response) => {
          const typedResponse = response as OwnerApiResponse;

          if (!typedResponse.success) {
            throw new Error(typedResponse.msg);
          }

          cursorRef.current[fk] = typedResponse.nextCursor;

          // Re-read current filter state for the latest checked options
          const latestFilter = filters.find((f) => f.value === fk);
          const prevOptions = latestFilter?.options ?? [];
          const checkedOptions = prevOptions.filter((o) => o.isChecked);
          const checkedValues = new Set(checkedOptions.map((o) => o.value));

          const newOptions = typedResponse.data.map((owner) => ({
            label:
              owner.type === 'user'
                ? `${owner.firstname ?? ''} ${owner.lastname ?? ''} (${
                    owner.username
                  })`
                : owner.displayname ?? '',
            value: String(owner.id),
            isChecked: checkedValues.has(String(owner.id)),
            type: owner.type,
          }));

          let mergedOptions: MenuMultiOption[];

          if (isAppend) {
            // Scroll: append new options, skip duplicates
            const existingValues = new Set(prevOptions.map((o) => o.value));
            const uniqueNew = newOptions.filter(
              (o) => !existingValues.has(o.value)
            );

            mergedOptions = [...prevOptions, ...uniqueNew];
          } else {
            // Search / init: checked first, then new unchecked
            const uncheckedNew = newOptions.filter(
              (o) => !checkedValues.has(o.value)
            );

            mergedOptions = [...checkedOptions, ...uncheckedNew];
          }

          updateFilter(fk, {
            options: mergedOptions,
            isLoading: false,
            hasMoreOptions: typedResponse.nextCursor !== null,
          });
        })
        .catch((error) => {
          cursorRef.current[fk] = null;
          updateFilter(fk, {
            isLoading: false,
            isError: true,
            hasMoreOptions: false,
          });

          Logger.log('Error loading owner filter options', error);
        });
    },
    [filters, getOwnersList, updateFilter]
  );

  /**
   * Loads options for the Name filter with cursor-based pagination and
   * checked-option-preservation logic.
   *
   * @param isInit - `true` for initial load or search change; `false` for scroll append.
   * @param searchOverride - When provided, uses this value instead of reading from Redux (avoids stale closures with debounced calls).
   */
  const loadNameOptions = useCallback(
    (isInit: boolean, searchOverride?: string) => {
      const fk = F_KEY_NM;
      const currentFilter = filters.find((f) => f.value === fk);

      if (!currentFilter) {
        return;
      }

      const searchText = searchOverride ?? currentFilter.searchText ?? '';
      const isAppend = !isInit && cursorRef.current[fk] != null;

      // Set loading state
      updateFilter(fk, { isLoading: true, isError: false });

      // On fresh search / init, reset cursor and keep only checked options
      if (isInit) {
        cursorRef.current[fk] = null;
        updateFilter(fk, {
          isLoading: true,
          isError: false,
          options: currentFilter.options.filter((o) => o.isChecked),
        });
      }

      getNameList({
        name: searchText,
        max: 50,
        cursor: cursorRef.current[fk],
      })
        .unwrap()
        .then((response) => {
          const typedResponse = response as NameApiResponse;

          if (!typedResponse.success) {
            throw new Error(typedResponse.msg);
          }

          cursorRef.current[fk] = typedResponse.nextCursor;

          // Re-read current filter state for the latest checked options
          const latestFilter = filters.find((f) => f.value === fk);
          const prevOptions = latestFilter?.options ?? [];
          const checkedOptions = prevOptions.filter((o) => o.isChecked);
          const checkedValues = new Set(checkedOptions.map((o) => o.value));

          const newOptions = typedResponse.data.map((e) => ({
            label: e.name,
            value: e.name,
            isChecked: checkedValues.has(e.name),
          }));

          let mergedOptions: MenuMultiOption[];

          if (isAppend) {
            // Scroll: append new options, skip duplicates
            const existingValues = new Set(prevOptions.map((o) => o.value));
            const uniqueNew = newOptions.filter(
              (o) => !existingValues.has(o.value)
            );

            mergedOptions = [...prevOptions, ...uniqueNew];
          } else {
            // Search / init: checked first, then new unchecked
            const uncheckedNew = newOptions.filter(
              (o) => !checkedValues.has(o.value)
            );

            mergedOptions = [...checkedOptions, ...uncheckedNew];
          }

          updateFilter(fk, {
            options: mergedOptions,
            isLoading: false,
            hasMoreOptions: false,
          });
        })
        .catch((error) => {
          cursorRef.current[fk] = null;
          updateFilter(fk, {
            isLoading: false,
            isError: true,
            hasMoreOptions: false,
          });

          Logger.log('Error loading name filter options', error);
        });
    },
    [filters, getNameList, updateFilter]
  );

  /**
   * Main entry point — dispatches to the correct loader based on filter key.
   *
   * @param fk - The filter key (e.g. `F_KEY_ON`).
   * @param isInit - `true` for initial load or search change; `false` for scroll append.
   * @param searchText - When provided, forwarded to the loader so the debounced call carries the correct value.
   */
  const loadFilterOptions = useCallback(
    (fk: string, isInit = false, searchText?: string) => {
      switch (fk) {
        case F_KEY_ON:
          loadOwnerFilterOptions(isInit, searchText);
          break;
        case F_KEY_NM:
          loadNameOptions(isInit, searchText);
          break;
        default:
          break;
      }
    },
    [loadOwnerFilterOptions, loadNameOptions]
  );

  return { loadFilterOptions };
};

// eslint-disable-next-line import/prefer-default-export
export { useLoadFilterOptions };
